import { queryKeys, type TaskDetail, type TaskReply } from '@mevaser/shared'
import { Skeleton } from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, StickyNote } from 'lucide-react'
import { api, apiBaseUrl } from '../lib/api.js'
import { AttachmentList } from './attachments.js'
import { LinkifiedText } from './linkified-text.js'

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })

/** The row's expand/collapse chevron. Safe inside a clickable row or a link. */
export function ExpandToggle({
  expanded,
  onToggle,
  label,
}: {
  expanded: boolean
  onToggle: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        onToggle()
      }}
      className="shrink-0 text-foreground-muted hover:text-foreground"
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} notes for ${label}`}
    >
      {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
    </button>
  )
}

interface TicketMessage {
  id: string
  type: string
  body: string
  staffName: string | null
  attachments?: { id: string; filename: string; contentType: string; sizeBytes: number }[]
  createdAt: string
}

/** A ticket's internal notes, oldest first (customer messages stay on the ticket page). */
export function TicketNotesPreview({ ticketId }: { ticketId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.supportTickets.messages(ticketId),
    queryFn: () => api.get<TicketMessage[]>(`/support-tickets/${ticketId}/messages`),
    refetchInterval: 15_000,
  })
  const notes = (data ?? []).filter((m) => m.type === 'note')

  return (
    <div className="space-y-2 text-sm">
      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : notes.length === 0 ? (
        <p className="text-foreground-muted">No internal notes yet.</p>
      ) : (
        <ol className="max-h-80 space-y-2 overflow-y-auto">
          {notes.map((note) => (
            <li
              key={note.id}
              className="flex items-start gap-2 rounded-md border border-dashed border-border bg-surface px-3 py-2"
            >
              <StickyNote
                className="mt-0.5 size-3.5 shrink-0 text-foreground-subtle"
                aria-hidden="true"
              />
              <div className="min-w-0">
                {note.body ? (
                  <p className="whitespace-pre-wrap">
                    <LinkifiedText text={note.body} />
                  </p>
                ) : null}
                <AttachmentList
                  compact
                  files={note.attachments?.map((a) => ({
                    ...a,
                    url: `${apiBaseUrl}/support-tickets/${ticketId}/attachments/${a.id}`,
                  }))}
                />
                <p className="mt-0.5 text-2xs text-foreground-muted">
                  {note.staffName ?? 'Staff'} · {formatWhen(note.createdAt)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
      <Link
        to="/support-tickets/$ticketId"
        params={{ ticketId }}
        className="text-xs text-primary hover:underline"
      >
        Open ticket →
      </Link>
    </div>
  )
}

/** A task's description, completion note and reply thread (oldest first). */
export function TaskNotesPreview({ taskId }: { taskId: string }) {
  const { data: task, isLoading } = useQuery({
    queryKey: queryKeys.tasks.detail(taskId),
    queryFn: () => api.get<TaskDetail>(`/tasks/${taskId}`),
    refetchInterval: 15_000,
  })
  const { data: replies } = useQuery({
    queryKey: queryKeys.tasks.replies(taskId),
    queryFn: () => api.get<TaskReply[]>(`/tasks/${taskId}/replies`),
    refetchInterval: 15_000,
  })

  return (
    <div className="space-y-3 text-sm">
      {isLoading || !task ? (
        <Skeleton className="h-10 w-full" />
      ) : (
        <div className="max-h-80 space-y-3 overflow-y-auto">
          <div>
            {task.details ? (
              <p className="whitespace-pre-wrap">
                <LinkifiedText text={task.details} />
              </p>
            ) : (
              <p className="text-foreground-muted">No description.</p>
            )}
            <AttachmentList files={task.attachments} compact />
          </div>
          {task.status !== 'open' && task.completionNote ? (
            <div className="border-t border-border pt-2">
              <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                Completion note
              </p>
              <p className="whitespace-pre-wrap">
                <LinkifiedText text={task.completionNote} />
              </p>
            </div>
          ) : null}
          <div className="border-t border-border pt-2">
            <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Replies</p>
            {(replies ?? []).length === 0 ? (
              <p className="text-foreground-muted">No replies yet.</p>
            ) : (
              <ol className="mt-1 space-y-2">
                {(replies ?? []).map((reply) => (
                  <li key={reply.id} className="rounded-md border border-border bg-surface p-2">
                    <p className="text-2xs text-foreground-muted">
                      <span className="font-medium text-foreground">
                        {reply.authorName ?? 'Staff'}
                      </span>{' '}
                      · {formatWhen(reply.createdAt)}
                    </p>
                    {reply.body ? (
                      <p className="mt-1 whitespace-pre-wrap">
                        <LinkifiedText text={reply.body} />
                      </p>
                    ) : null}
                    <AttachmentList files={reply.attachments} compact />
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}
      <Link
        to="/tasks/$taskId"
        params={{ taskId }}
        className="text-xs text-primary hover:underline"
      >
        Open task →
      </Link>
    </div>
  )
}
